import { removePPID } from '../helpers/removePPID'
import { TCF } from '../tcf/TCF'
import {
  TCF_API_VERSION,
  TCF_PURPOSE_1,
  TCF_VENDOR_ID_GOOGLE,
  TCF_VENDOR_ID_MEDIAVINE
} from '../../constants'
import Logger from '../../helpers/Logger'
import type WebModel from '../../models/WebModel'

export class GDPR implements IGDPR {
  public enableGdpr: boolean = false
  private gdprDone: boolean = false
  private hasValidPurposes: boolean = false
  private hasValidVendors: boolean = false
  private readyCallbackStack: Array<Function> = []
  private noConsentCallbackStack: Array<Function> = []

  constructor(private readonly model: WebModel) {
    this.enableGdpr = true
  }

  /**
   * Initializes the GDPR Preact modal and privacy link
   */
  public async initModal(): Promise<any> {
    try {
      await this.loadAssets()
    } catch (error) {
      const errorMessage = 'GDPR: Error loading "gdprModal" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$adManagementConfig.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    }
    this.setupCmp()
  }

  public onNoConsent(callback: Function): void {
    // Safe to call removePPID() because this (GDPR) object is only instantiated if
    // GDPR is required, i.e., user is located in Europe.
    removePPID(this.model)
    // We're already done, execute callbacks immediately
    if (this.gdprDone && (!this.hasValidPurposes || !this.hasValidVendors)) {
      return callback()
    }

    // Wait for CMP to be accepted or loaded
    if (this.enableGdpr) {
      this.noConsentCallbackStack.push(callback)
      return
    }
  }

  /** Handles GDPR onReady callback listeners */
  public onReady(callback: Function): void {
    // Halt the wrapper if we do not have valid consents
    if (this.gdprDone && (!this.hasValidPurposes || !this.hasValidVendors)) {
      Logger.custom(
        ['debug', 'gpdr'],
        'GDPR: Halting wrapper execution due to lack of consent'
      )
    }
    // We're already done, execute callbacks immediately
    if (this.gdprDone && this.hasValidPurposes && this.hasValidVendors) {
      return callback()
    }
    // Wait for CMP to be accepted or loaded
    if (this.enableGdpr) {
      this.readyCallbackStack.push(callback)
      return
    }
  }

  /** Validates specific purposes for consent */
  private validatePurposes(): void {
    const validPurposeIds = [TCF_PURPOSE_1]
    const consents = TCF.getPurposeConsents(validPurposeIds)
    this.hasValidPurposes = validPurposeIds.every((id) => consents.includes(id))
  }

  /** Validates specific vendors for consent */
  private validateVendors(): void {
    const validConsentIds = [TCF_VENDOR_ID_GOOGLE, TCF_VENDOR_ID_MEDIAVINE]
    const validLegitIntIds = [TCF_VENDOR_ID_GOOGLE]
    const consents = TCF.getVendorConsents(validConsentIds)
    const legitInts = TCF.getVendorLegitimateInterests(validLegitIntIds)
    const hasValidConsent = validConsentIds.every((id) => consents.includes(id))
    const hasValidLegitInt = validLegitIntIds.every((id) =>
      legitInts.includes(id)
    )
    this.hasValidVendors = hasValidConsent && hasValidLegitInt
  }

  /** Handles applicable onReady callback listeners */
  private isReady(): void {
    const data = TCF.tcModel || {}
    window.$adManagementConfig.newrelic.setCustomAttribute(
      'PurposeConsents',
      data.purposeConsents?.size
    )
    window.$adManagementConfig.newrelic.setCustomAttribute(
      'PurposeLegitimateInterests',
      data.purposeLegitimateInterests?.size
    )

    this.gdprDone = true
    this.validatePurposes()
    this.validateVendors()

    // Only proceed if we have appropriate consents
    if (this.hasValidPurposes && this.hasValidVendors) {
      let func = this.readyCallbackStack.pop()
      while (func) {
        func()
        func = this.readyCallbackStack.pop()
      }
    } else {
      Logger.custom(
        ['debug', 'gpdr'],
        'GDPR: Halting wrapper execution due to lack of consent'
      )
      let func = this.noConsentCallbackStack.pop()
      while (func) {
        func()
        func = this.noConsentCallbackStack.pop()
      }
    }
  }

  /** Dynamically imports GDPR Preact modal */
  private loadAssets(): Promise<any> {
    return import(/* webpackChunkName: "gdprModal" */ './assets/App')
  }

  /** Add CmpApi event handlers */
  private setupCmp(): void {
    window.__tcfapi(
      'addEventListener',
      TCF_API_VERSION,
      this.tcfApiEventHandler.bind(this)
    )
  }

  /** Handles tcData eventStatuses */
  private tcfApiEventHandler(tcData: TcData, success: boolean): void {
    switch (success && tcData.eventStatus) {
      case TcDataEventStatus.cmpuishown: {
        Logger.debug('GDPR: TCF GDPR UI Shown')
        window.$adManagementConfig.newrelic.addPageAction('gdprModal', {
          state: 'CMP Displayed',
          wrapperGroup: window.$adManagementConfig.web.model.versionGroup.name
        })
        break
      }
      case TcDataEventStatus.tcloaded: {
        Logger.debug('GDPR: TFC TC Loaded')
        window.$adManagementConfig.newrelic.addPageAction('gdprModal', {
          state: 'CMP Previously Accepted',
          wrapperGroup: window.$adManagementConfig.web.model.versionGroup.name
        })
        this.isReady()
        break
      }
      case TcDataEventStatus.useractioncomplete: {
        Logger.debug('GDPR: TCF GDPR user action complete')
        window.$adManagementConfig.newrelic.addPageAction('gdprModal', {
          state: 'CMP Accepted',
          wrapperGroup: window.$adManagementConfig.web.model.versionGroup.name
        })
        this.isReady()
        break
      }
    }
  }
}

enum TcDataEventStatus {
  tcloaded = 'tcloaded',
  cmpuishown = 'cmpuishown',
  useractioncomplete = 'useractioncomplete'
}

interface TcData {
  eventStatus: string
}
